import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import { ImageSize } from '@nxte-nl/image-hooks';

import { modalHooks, UseModalResult } from '../../hooks/modalHooks/modalHooks';
import { ImagePresentational } from '../../types/common';
import { ImageWithSize } from '../ImageWithSize/ImageWithSize';
import { ImageWithSizeLoadingBlock } from '../ImageWithSizeLoadingBlock/ImageWithSizeLoadingBlock';
import { Link, LinkIconPosition, LinkIntent, LinkType } from '../Link/Link';
import { NarrowModal } from '../NarrowModal/NarrowModal';
import { Typography, TypographyColor } from '../Typography/Typography';
import styles from './ProductDetailsBrandsBlock.module.css';

export const NUMBER_OF_BRANDS_MOBILE = 9;
export const NUMBER_OF_BRANDS_DESKTOP = 12;

export const hooks = {
  useShowAllBrandsLink(
    brands: ImagePresentational[],
    isMobile: boolean,
  ): {
    brandsToRender: ImagePresentational[];
    isShowAllBrandsLink: boolean;
    numberOfBrands: number;
  } {
    const numberOfBrands = brands.length;
    const isShowAllBrandsLink = isMobile
      ? numberOfBrands > NUMBER_OF_BRANDS_MOBILE
      : numberOfBrands > NUMBER_OF_BRANDS_DESKTOP;
    const brandsToRender = React.useMemo(
      () =>
        isMobile
          ? brands.slice(0, NUMBER_OF_BRANDS_MOBILE)
          : brands.slice(0, NUMBER_OF_BRANDS_DESKTOP),
      [brands, isMobile],
    );
    return { numberOfBrands, isShowAllBrandsLink, brandsToRender };
  },
};

export type Props = {
  brands: ImagePresentational[];
  brandsModalProps: UseModalResult;
  className?: string;
  isMobile: boolean;
};

const messages = defineMessages({
  partnersTitle: {
    id: 'src.components.ProductDetailsBrandsBlock.partnersTitle',
    defaultMessage: 'Exchange for',
  },
  brandsLinkText: {
    id: 'src.components.ProductDetailsBrandsBlock.brandsLinkText',
    defaultMessage: 'View all {numberOfBrands} partners',
  },
  modalTitle: {
    id: 'src.components.ProductDetailsBrandsBlock.modalTitle',
    defaultMessage: 'Our partners',
  },
});

export const ProductDetailsBrandsBlock = React.memo((props: Props) => {
  const {
    brands,
    className,
    isMobile,
    brandsModalProps: {
      isOpenModal: isModalOpen,
      onModalClose: handleCloseModal,
      onModalOpen,
    },
  } = props;
  const { numberOfBrands, isShowAllBrandsLink, brandsToRender } = hooks.useShowAllBrandsLink(
    brands,
    isMobile,
  );

  const handleOnClick = modalHooks.useOnModalOpenWithoutCloseOtherModals(onModalOpen);

  if (brands.length === 0) {
    return null;
  }

  return (
    <>
      <div className={classNames(styles.productDetailsBrandsBlock, className)}>
        <Typography className={styles.title} variant='body2'>
          <FormattedMessage {...messages.partnersTitle} />
        </Typography>
        <ul className={styles.brandsList}>
          {brandsToRender.map((brand) => (
            <li key={brand.url.href} className={styles.brandItem}>
              <ImageWithSize
                image={brand}
                size={ImageSize.M}
                className={styles.brandImage}
              />
            </li>
          ))}
        </ul>
        {isShowAllBrandsLink && (
          <Link
            type={LinkType.Span}
            onClick={handleOnClick}
            intent={LinkIntent.primary}
            iconPosition={LinkIconPosition.Right}
            className={styles.brandLink}
          >
            <Typography color={TypographyColor.LinkPrimary}>
              <FormattedMessage
                {...messages.brandsLinkText}
                values={{ numberOfBrands: numberOfBrands.toString() }}
              />
            </Typography>
          </Link>
        )}
      </div>
      <NarrowModal
        isOpen={isModalOpen()}
        onClose={handleCloseModal}
        title={<FormattedMessage {...messages.modalTitle} />}
        modalHeaderClassName={styles.modalHeader}
        modalContentClassName={styles.modalContent}
      >
        <ul className={styles.brandsList}>
          {brands.map((brand) => (
            <li key={brand.url.href} className={styles.brandItem}>
              <ImageWithSizeLoadingBlock
                image={brand}
                size={ImageSize.M}
                className={styles.brandImage}
                width={93}
                height={45}
                loadingBlockStyle={{
                  borderRadius: '2px',
                }}
              />
            </li>
          ))}
        </ul>
      </NarrowModal>
    </>
  );
});

ProductDetailsBrandsBlock.displayName = 'ProductDetailsBrandsBlock';
